import React from 'react';
import {
  Volume2,
  VolumeX,
  Square,
  BookAudio,
  AudioLines,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
// ActiveSoundRow — compact row for an active sound in the mixer
// ─────────────────────────────────────────────────────────────────────────────
const ActiveSoundRow = ({ sound, volume, defaultVolume, onStop, onSetVolume }) => {
  const currentVolume = volume ?? defaultVolume;

  return (
    <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1.5">
      {/* Animated indicator */}
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
      </span>

      {/* Name + loop badge */}
      <div className="min-w-0 flex-1 flex items-center gap-1.5">
        <span className="text-xs font-medium truncate">{sound.displayName || sound.name}</span>
        {sound.isLoop && (
          <Badge
            variant="outline"
            className="text-[9px] px-1 py-0 h-4 border-primary/40 text-primary shrink-0"
          >
            LOOP
          </Badge>
        )}
      </div>

      {/* Volume slider */}
      <div className="flex items-center gap-1 w-24 shrink-0">
        <Slider
          value={[currentVolume * 100]}
          onValueChange={([val]) => onSetVolume(sound.id, val / 100)}
          max={100}
          step={1}
          className="flex-1"
          aria-label={`Volume ${sound.name}`}
        />
        <span className="text-[9px] text-muted-foreground tabular-nums w-5 text-right">
          {Math.round(currentVolume * 100)}
        </span>
      </div>

      {/* Stop button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 hover:bg-destructive/20 hover:text-destructive"
        onClick={() => onStop(sound.id)}
        aria-label={`Stop ${sound.name}`}
      >
        <Square className="h-3 w-3" />
      </Button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SoundboardPanel — compact mixer view in the floating player card
// ─────────────────────────────────────────────────────────────────────────────
const SoundboardPanel = ({
  activeSounds,
  soundVolumes,
  masterVolume,
  defaultSoundVolume,
  onStopSound,
  onStopAll,
  onSetSoundVolume,
  onSetMasterVolume,
  onOpenLibrary,
  lang = 'fr',
}) => {
  const hasActiveSounds = activeSounds.length > 0;

  return (
    <div className="space-y-3">
      {/* Active sounds header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AudioLines className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">
            {lang === 'fr' ? 'Sons actifs' : 'Active sounds'}
          </span>
          {hasActiveSounds && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
              {activeSounds.length}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1">
          {hasActiveSounds && (
            <Button
              variant="destructive"
              size="sm"
              className="h-7 text-xs"
              onClick={onStopAll}
            >
              <Square className="h-3 w-3 mr-1" />
              Stop all
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={onOpenLibrary}
          >
            <BookAudio className="h-3.5 w-3.5 mr-1" />
            {lang === 'fr' ? 'Sons' : 'Sounds'}
          </Button>
        </div>
      </div>

      {/* Active sounds list */}
      {hasActiveSounds ? (
        <ScrollArea className={cn(activeSounds.length > 4 && 'h-[140px]')}>
          <div className="space-y-1.5 pr-1">
            {activeSounds.map((sound) => (
              <ActiveSoundRow
                key={sound.id}
                sound={sound}
                volume={soundVolumes[sound.id]}
                defaultVolume={defaultSoundVolume}
                onStop={onStopSound}
                onSetVolume={onSetSoundVolume}
              />
            ))}
          </div>
        </ScrollArea>
      ) : (
        <div className="text-center py-4 text-muted-foreground">
          <AudioLines className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="text-xs">{lang === 'fr' ? 'Aucun son actif' : 'No active sounds'}</p>
          <p className="text-[10px] mt-0.5">
            {lang === 'fr' ? 'Ouvrez la bibliothèque pour lancer des sons' : 'Open the library to play sounds'}
          </p>
        </div>
      )}

      <Separator />

      {/* Master volume */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex items-center justify-center h-6 w-6 rounded transition-colors hover:bg-accent text-muted-foreground shrink-0"
          onClick={() => onSetMasterVolume(masterVolume > 0 ? 0 : 0.8)}
          aria-label={masterVolume > 0 ? (lang === 'fr' ? 'Couper le son' : 'Mute') : (lang === 'fr' ? 'Activer le son' : 'Unmute')}
        >
          {masterVolume > 0 ? (
            <Volume2 className="h-4 w-4" />
          ) : (
            <VolumeX className="h-4 w-4" />
          )}
        </button>
        <Slider
          value={[masterVolume * 100]}
          onValueChange={([val]) => onSetMasterVolume(val / 100)}
          max={100}
          step={1}
          className="flex-1"
          aria-label="Volume master soundboard"
        />
        <span className="w-10 text-right text-xs text-muted-foreground tabular-nums">
          {Math.round(masterVolume * 100)}%
        </span>
      </div>
    </div>
  );
};

export default SoundboardPanel;
